import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { deriveBusinessDisplayName } from '../utils/business';

const SelectionContext = createContext(undefined);

export const SelectionProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedBusinessId, setSelectedBusinessIdState] = useState('');
  const [selectedBusinessName, setSelectedBusinessNameState] = useState('');
  const [selectedBranchId, setSelectedBranchIdState] = useState('');
  const storageKey = React.useMemo(() => (user ? `mfs-selection:${user.uid}` : null), [user?.uid]);
  const derivedName = useCallback(
    (businessId, candidateName) => deriveBusinessDisplayName({
      businessId,
      businessName: candidateName,
    }),
    [],
  );

  useEffect(() => {
    if (!user) {
      setSelectedBusinessIdState('');
      setSelectedBusinessNameState('');
      setSelectedBranchIdState('');
    } else if (typeof window !== 'undefined' && storageKey) {
      try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object') {
            const restoredId = parsed.businessId || '';
            const restoredName = derivedName(restoredId, parsed.businessName);
            setSelectedBusinessIdState(restoredId);
            setSelectedBusinessNameState(restoredId ? restoredName : '');
            setSelectedBranchIdState(parsed.branchId || '');
          }
        }
      } catch (error) {
        console.error('Failed to restore selection state:', error);
      }
    }
  }, [user, storageKey, derivedName]);

  const persistSelection = useCallback((businessId, businessName, branchId) => {
    if (typeof window === 'undefined' || !storageKey) return;
    try {
      const payload = {
        businessId: businessId || '',
        businessName: businessName || '',
        branchId: branchId || '',
      };
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to persist selection state:', error);
    }
  }, [storageKey]);

  const setSelectedBusinessId = useCallback((businessId, businessName = '') => {
    const normalizedBusinessId = businessId || '';
    const normalizedBusinessName = normalizedBusinessId
      ? derivedName(
        normalizedBusinessId,
        businessName || (normalizedBusinessId === selectedBusinessId ? selectedBusinessName : ''),
      )
      : '';
    const normalizedBranchId = normalizedBusinessId && normalizedBusinessId === selectedBusinessId
      ? selectedBranchId
      : '';

    setSelectedBusinessIdState(normalizedBusinessId);
    setSelectedBusinessNameState(normalizedBusinessName);
    setSelectedBranchIdState(normalizedBranchId);

    persistSelection(normalizedBusinessId, normalizedBusinessName, normalizedBranchId);
  }, [persistSelection, selectedBranchId, selectedBusinessId, selectedBusinessName, derivedName]);

  const setSelectedBranchId = useCallback((branchId) => {
    const normalizedBranchId = branchId || '';
    setSelectedBranchIdState(normalizedBranchId);
    persistSelection(selectedBusinessId, selectedBusinessName, normalizedBranchId);
  }, [persistSelection, selectedBusinessId, selectedBusinessName]);

  const clearSelections = useCallback(() => {
    setSelectedBusinessIdState('');
    setSelectedBusinessNameState('');
    setSelectedBranchIdState('');
    if (typeof window !== 'undefined' && storageKey) {
      window.localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const clearBranch = useCallback(() => {
    setSelectedBranchIdState('');
    persistSelection(selectedBusinessId, selectedBusinessName, '');
  }, [persistSelection, selectedBusinessId, selectedBusinessName]);

  const value = useMemo(() => ({
    selectedBusinessId,
    setSelectedBusinessId,
    selectedBusinessName,
    selectedBranchId,
    setSelectedBranchId,
    clearSelections,
    clearBranch,
  }), [
    selectedBusinessId,
    setSelectedBusinessId,
    selectedBusinessName,
    selectedBranchId,
    setSelectedBranchId,
    clearSelections,
    clearBranch,
  ]);

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};
